import { useState, useEffect, useMemo } from 'preact/hooks'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'
import { addProductToWishlist, removeProductInWishlist, getWishlist } from 'libs/the-wishlist/wishlist-service'

export default function useWishlist(product, variantId, isProductForm = false) {
  const { id } = window.GM_STATE.customer || {}
  const [wishlist, setWishlist] = useState(() => window?.WISHLIST_DATA || null)
  const [isAdded, setIsAdded] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const isVariantInWishlist = useMemo(() => {
    if (!wishlist?.items?.item || !product?.id) return false

    return wishlist.items.item.some((item) => {
      const productMatch = item.product?.id?.toString() === product.id.toString()

      if (!productMatch) return false
      if (!isProductForm) return true

      return item.product?.variants?.some((variant) => variant.id?.toString() === variantId?.toString())
    })
  }, [wishlist, variantId, product?.id, isProductForm])

  const updateWishlist = async () => {
    if (!id) return

    try {
      const res = await getWishlist(id)
      const fetchedWishlist = res?.data?.wishlists?.[0]

      if (fetchedWishlist) {
        globalEmitter.emit(emitterProps.wishlist.change, fetchedWishlist)
      }
    } catch (err) {
      console.error('Error fetching wishlist:', err)
    }
  }

  const handleWishlist = async () => {
    if (!product?.id || !variantId || !wishlist?.id) return

    try {
      setIsLoading(true)

      if (isAdded) {
        const matchingItem = wishlist.items.item.find((group) => {
          const variant = group.product?.variants?.[0]

          return variant?.id?.toString() === variantId.toString()
        })

        const response = await removeProductInWishlist({
          is_add_to_cart: false,
          wishlist_id: wishlist.id,
          wishlist_uuid: '',
          product_id: product.id.toString(),
          variant_id: variantId.toString(),
          item_uuid: '',
          item_id: matchingItem.id
        })

        if (response?.success) {
          globalEmitter.emit(emitterProps.notice.global, {
            type: 'success',
            content: translate.wishlist.remove
          })
        }
      } else {
        const response = await addProductToWishlist({
          product_id: product.id.toString(),
          variant_id: variantId.toString(),
          wishlist_ids: [wishlist.id]
        })

        if (response?.success) {
          globalEmitter.emit(emitterProps.notice.global, {
            type: 'success',
            content: translate.wishlist.add
          })
        }
      }

      await updateWishlist()
    } catch (err) {
      console.error('Wishlist update failed:', err)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    const onWishlistUpdate = (value) => {
      setWishlist(value)
    }

    globalEmitter.on(emitterProps.wishlist.init, onWishlistUpdate)
    globalEmitter.on(emitterProps.wishlist.change, onWishlistUpdate)

    return () => {
      globalEmitter.off(emitterProps.wishlist.init, onWishlistUpdate)
      globalEmitter.off(emitterProps.wishlist.change, onWishlistUpdate)
    }
  }, [])

  useEffect(() => {
    setIsAdded(isVariantInWishlist)
  }, [isVariantInWishlist])

  return {
    wishlist,
    isAdded,
    handleWishlist,
    isLoading
  }
}
